// src/components/UI/BuyCreditsModal.js
import React, { useState, useEffect, useContext } from 'react';
import { FaTimes, FaCoins, FaRocket, FaStar, FaCrown, FaPlus, FaMinus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styles from './BuyCreditsModal.module.css';
import AuthContext from '../../context/AuthContext';
import { createRazorpayOrder, verifyRazorpayPayment, initializeRazorpayPayment } from '../../services/paymentApi';

const BuyCreditsModal = ({ show, onClose, onPurchase }) => {
    const [activeTab, setActiveTab] = useState('bundles'); // 'bundles' or 'custom'
    const [selectedBundle, setSelectedBundle] = useState(null);
    const [customTokens, setCustomTokens] = useState(0);
    const [showLimitMessage, setShowLimitMessage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();

    // Pricing constants
    const TOKEN_PRICE = 2.50; // ₹2.50 per token
    const MAX_CUSTOM_TOKENS = 10000; // Maximum tokens for custom purchase

    // Preset bundles - 3 token-only options
    const bundles = [
        {
            id: 'starter',
            name: 'Starter Pack',
            type: 'tokens',
            tokens: 200,
            price: 450,
            originalPrice: 500,
            popular: false,
            icon: FaRocket,
            description: 'Perfect for getting started'
        },
        {
            id: 'popular',
            name: 'Popular Pack',
            type: 'tokens',
            tokens: 500,
            price: 1000,
            originalPrice: 1250,
            popular: true,
            icon: FaStar,
            description: 'Most chosen by our users'
        },
        {
            id: 'pro',
            name: 'Pro Pack',
            type: 'tokens',
            tokens: 1000,
            price: 1750,
            originalPrice: 2500,
            popular: false,
            icon: FaCrown,
            description: 'Maximum value for power users'
        }
    ];

    // Reset states when modal opens/closes or tab changes
    useEffect(() => {
        if (!show) {
            setShowLimitMessage(false);
            setCustomTokens(0);
            setSelectedBundle(null);
        }
    }, [show]);

    useEffect(() => {
        setShowLimitMessage(false);
    }, [activeTab]);

    const handleBundleSelect = (bundle) => {
        setSelectedBundle(bundle);
    };

    const handleCustomChange = (increment) => {
        const newValue = Math.max(0, customTokens + increment);

        if (newValue > MAX_CUSTOM_TOKENS) {
            setShowLimitMessage(true);
            setCustomTokens(MAX_CUSTOM_TOKENS);
        } else {
            setCustomTokens(newValue);
            if (newValue <= MAX_CUSTOM_TOKENS && showLimitMessage) {
                // Hide message if user goes back within limit
                setShowLimitMessage(false);
            }
        }
    };

    const handleCustomInputChange = (value) => {
        const numValue = Math.max(0, parseInt(value) || 0);

        if (numValue > MAX_CUSTOM_TOKENS) {
            setShowLimitMessage(true);
            setCustomTokens(MAX_CUSTOM_TOKENS);
        } else {
            setCustomTokens(numValue);
            if (numValue <= MAX_CUSTOM_TOKENS && showLimitMessage) {
                // Hide message if user goes back within limit
                setShowLimitMessage(false);
            }
        }
    };

    const calculateCustomPrice = () => {
        return customTokens * TOKEN_PRICE;
    };

    const handlePurchase = async () => {
        if (!authCtx.token) {
            alert('Please login to purchase tokens');
            return;
        }

        let tokensToAdd, amountToPay;

        if (activeTab === 'bundles' && selectedBundle) {
            tokensToAdd = selectedBundle.tokens;
            amountToPay = selectedBundle.price;
        } else if (activeTab === 'custom' && customTokens > 0) {
            tokensToAdd = customTokens;
            amountToPay = calculateCustomPrice();
        } else {
            alert('Please select a package or enter a custom amount');
            return;
        }

        setIsLoading(true);

        try {
            // Create Razorpay order
            const orderResponse = await createRazorpayOrder(
                authCtx.token,
                amountToPay,
                'tokens',
                { tokens: tokensToAdd }
            );

            if (!orderResponse.success) {
                throw new Error(orderResponse.message || 'Failed to create order');
            }

            // Initialize Razorpay payment with all payment methods
            await initializeRazorpayPayment(
                orderResponse.order,
                async (paymentResponse) => {
                    try {
                        // Verify payment with backend
                        const verifyResponse = await verifyRazorpayPayment(
                            authCtx.token,
                            paymentResponse.razorpay_order_id,
                            paymentResponse.razorpay_payment_id,
                            paymentResponse.razorpay_signature
                        );

                        if (verifyResponse.success) {
                            // Update user credits in context with the actual balance from backend
                            if (authCtx.updateUserTokens && verifyResponse.user_balance) {
                                authCtx.updateUserTokens(verifyResponse.user_balance.credits);
                            }

                            // Close modal and show success message
                            onClose();
                            navigate('/dashboard', {
                                state: {
                                    message: `Successfully purchased ${tokensToAdd} credits! Your new balance: ${verifyResponse.user_balance?.credits || 0} credits`,
                                    type: 'success'
                                }
                            });
                        } else {
                            throw new Error('Payment verification failed');
                        }
                    } catch (error) {
                        alert('Payment verification failed. Please contact support if amount was deducted.');
                    }
                },
                (error) => {
                    if (error !== 'Payment cancelled by user') {
                        alert('Payment failed. Please try again.');
                    }
                }
            );
        } catch (error) {
            alert(error.message || 'Failed to initiate payment. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const isPurchaseDisabled = () => {
        if (activeTab === 'bundles') {
            return !selectedBundle;
        }
        return customTokens === 0;
    };

    if (!show) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Buy Credits</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                {/* Pricing Info */}
                <div className={styles.pricingInfo}>
                    <div className={styles.priceItem}>
                        <FaCoins className={styles.priceIcon} />
                        <span>1 Token = ₹{TOKEN_PRICE}</span>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className={styles.tabNavigation}>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'bundles' ? styles.active : ''}`}
                        onClick={() => setActiveTab('bundles')}
                    >
                        Preset Bundles
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'custom' ? styles.active : ''}`}
                        onClick={() => setActiveTab('custom')}
                    >
                        Custom Amount
                    </button>
                </div>


                {/* Content */}
                <div className={styles.modalBody}>
                    {activeTab === 'bundles' && (
                        <div className={styles.bundlesGrid}>
                            {bundles.map((bundle) => {
                                const Icon = bundle.icon;
                                const isSelected = selectedBundle?.id === bundle.id;
                                const discount = Math.round(((bundle.originalPrice - bundle.price) / bundle.originalPrice) * 100);

                                return (
                                    <div
                                        key={bundle.id}
                                        className={`${styles.bundleCard} ${isSelected ? styles.selected : ''} ${bundle.popular ? styles.popular : ''}`}
                                        onClick={() => handleBundleSelect(bundle)}
                                    >
                                        {bundle.popular && (
                                            <div className={styles.popularBadge}>Most Popular</div>
                                        )}

                                        <div className={styles.bundleIcon}>
                                            <Icon />
                                        </div>

                                        <h3 className={styles.bundleName}>{bundle.name}</h3>
                                        <p className={styles.bundleDescription}>{bundle.description}</p>

                                        <div className={styles.bundleDetails}>
                                            <div className={styles.bundleItem}>
                                                <FaCoins className={styles.bundleItemIcon} />
                                                <span>{bundle.tokens} Tokens</span>
                                            </div>
                                        </div>

                                        <div className={styles.bundlePricing}>
                                            <div className={styles.currentPrice}>₹{bundle.price}</div>
                                            {discount > 0 && (
                                                <>
                                                    <div className={styles.originalPrice}>₹{bundle.originalPrice}</div>
                                                    <div className={styles.discountBadge}>{discount}% OFF</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {activeTab === 'custom' && (
                        <div className={styles.customSection}>
                            <div className={styles.customControls}>
                                <div className={styles.customControl}>
                                    <label className={styles.customLabel}>
                                        <FaCoins className={styles.customIcon} />
                                        Tokens
                                    </label>
                                    <div className={styles.quantityControl}>
                                        <button
                                            className={styles.quantityButton}
                                            onClick={() => handleCustomChange(-1)}
                                            disabled={customTokens === 0}
                                        >
                                            <FaMinus />
                                        </button>
                                        <input
                                            type="number"
                                            value={customTokens}
                                            onChange={(e) => handleCustomInputChange(e.target.value)}
                                            className={styles.quantityInput}
                                            min="0"
                                            max={MAX_CUSTOM_TOKENS}
                                        />
                                        <button
                                            className={styles.quantityButton}
                                            onClick={() => handleCustomChange(1)}
                                            disabled={customTokens >= MAX_CUSTOM_TOKENS}
                                        >
                                            <FaPlus />
                                        </button>
                                    </div>
                                    <div className={styles.customPrice}>₹{(customTokens * TOKEN_PRICE).toFixed(2)}</div>
                                </div>
                            </div>

                            {/* Token Limit Message - Only show when limit is exceeded */}
                            {showLimitMessage && (
                                <div className={styles.limitInfo}>
                                    <p className={styles.limitText}>
                                        <strong>Maximum reached:</strong> {MAX_CUSTOM_TOKENS.toLocaleString()} tokens limit
                                    </p>
                                    <p className={styles.contactInfo}>
                                        Need more tokens? Contact us at{' '}
                                        <a href="mailto:info@ecombuddha.in" className={styles.emailLink}>
                                            info@ecombuddha.in
                                        </a>
                                        {' '}for bulk pricing.
                                    </p>
                                </div>
                            )}

                            <div className={styles.customTotal}>
                                <div className={styles.totalLabel}>Total Amount:</div>
                                <div className={styles.totalPrice}>₹{calculateCustomPrice().toFixed(2)}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={styles.modalFooter}>
                    <button className={styles.cancelButton} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={`${styles.purchaseButton} ${isPurchaseDisabled() || isLoading ? styles.disabled : ''}`}
                        onClick={handlePurchase}
                        disabled={isPurchaseDisabled() || isLoading}
                    >
                        <FaCoins className={styles.buttonIcon} />
                        {isLoading 
                            ? 'Processing...'
                            : activeTab === 'bundles' && selectedBundle
                            ? `Buy for ₹${selectedBundle.price}`
                            : activeTab === 'custom'
                            ? `Buy for ₹${calculateCustomPrice().toFixed(2)}`
                            : 'Select an option'
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BuyCreditsModal;